/**NIRSALEvidenceVault — DMRV Evidence Requirements */
import React, { useEffect, useState } from "react";
import { useNIRSAL } from "@/context/NIRSALContext";
import { PageHeader, SectionCard } from "@/components/ds";
import { toast } from "sonner";
import client from "@/api/client";
import { Upload, CheckCircle, AlertCircle } from "lucide-react";

const EVIDENCE_ITEMS = [
  { id:"kyc", label:"Borrower Onboarding / KYC", tier:"T1", critical:true },
  { id:"geo", label:"Geo-tagged Asset Photos/Coordinates", tier:"T1", critical:true },
  { id:"pfi_appraisal", label:"PFI Appraisal Records", tier:"T1", critical:true },
  { id:"crg_approval", label:"CRG Approval / Guarantee Records", tier:"T1", critical:true },
  { id:"equipment_invoice", label:"Equipment Invoices / Supply Agreement", tier:"T1", critical:false },
  { id:"oem_installer", label:"OEM / Installer Records", tier:"T1", critical:false },
  { id:"field_verification", label:"Field Verification Photos/Videos", tier:"T2", critical:false },
  { id:"telemetry", label:"Telemetry / Device Logs (where applicable)", tier:"T2", critical:false },
];

export default function NIRSALEvidenceVault() {
  const { consolidated, selectedPeriod, loading: ctxLoading } = useNIRSAL();
  const [evData, setEvData] = useState(null);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    if (!selectedPeriod) return;
    client.get(`/nirsal/evidence-status/${selectedPeriod}`).then(r => setEvData(r.data)).catch(() => {});
  }, [selectedPeriod]);

  const handleMark = async (clusterId, newCount) => {
    setUploading(u => ({...u, [clusterId]: true}));
    try {
      await client.post("/nirsal/evidence-status", { cluster_id: clusterId, period_id: selectedPeriod, items_uploaded: newCount, items_required: 8 });
      const r = await client.get(`/nirsal/evidence-status/${selectedPeriod}`);
      setEvData(r.data);
      toast.success("Evidence status updated");
    } catch { toast.error("Update failed"); }
    finally { setUploading(u => ({...u, [clusterId]: false})); }
  };

  const portfolioPct = evData?.portfolio_pct || 0;
  const clusters = evData?.by_cluster || [];

  if (ctxLoading) return <div className="p-8 text-slate-400 text-sm">Loading evidence vault…</div>;

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-6">
      <PageHeader label="EVIDENCE VAULT" title="" />
      <p className="text-xs text-slate-400 -mt-4">DMRV-backed evidence · T1/T2/T3 tiers · Confidence scoring · Article 6.4 readiness</p>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-slate-600">Portfolio Evidence Coverage</p>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${portfolioPct >= 70 ? "bg-emerald-100 text-emerald-700" : portfolioPct >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-50 text-red-600"}`}>
            {portfolioPct.toFixed(1)}% — {portfolioPct >= 70 ? "High" : portfolioPct >= 40 ? "Medium" : "Low"} Confidence
          </span>
        </div>
        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: portfolioPct + "%" }} />
        </div>
        <p className="text-[10px] text-slate-400 mt-2">VRS = Calc completeness (60%) + Evidence coverage (40%). Article 6.4 requires ≥70% for DMRV validation</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Evidence requirements */}
        <SectionCard title="Required Evidence Items">
          <div className="p-4 space-y-2">
            {EVIDENCE_ITEMS.map(item => (
              <div key={item.id} className="flex items-center gap-3 py-2 border-b border-slate-50">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${item.tier === "T1" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{item.tier}</span>
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-700">{item.label}</p>
                  {item.critical && <p className="text-[10px] text-red-500">Critical — required for Art.6.4 DMRV</p>}
                </div>
                <button className="text-[10px] text-blue-500 flex items-center gap-1 hover:underline">
                  <Upload size={10} /> Upload
                </button>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Per-cluster status */}
        <SectionCard title="Cluster Evidence Status">
          <div className="p-4 space-y-3">
            {clusters.map(c => {
              const pct = c.pct_uploaded || 0;
              return (
                <div key={c.cluster_id} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-slate-700 truncate max-w-[160px]">{c.name || c.cluster_id}</p>
                    <div className="flex items-center gap-1">
                      {pct >= 70 ? <CheckCircle size={12} className="text-emerald-500" /> : <AlertCircle size={12} className="text-amber-500" />}
                      <span className="text-xs font-bold text-slate-600">{pct.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mb-2">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: pct + "%" }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-slate-400">{c.items_uploaded}/{c.items_required} items · {c.state}</p>
                    <div className="flex gap-1">
                      <button disabled={uploading[c.cluster_id]} onClick={() => handleMark(c.cluster_id, Math.min(c.items_required, (c.items_uploaded||0) + 1))}
                        className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 disabled:opacity-50" data-testid={`mark-evidence-${c.cluster_id}`}>
                        +1 Item
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {clusters.length === 0 && <p className="text-xs text-slate-400">No evidence data yet — seed period data first</p>}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
